import React, { useState, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  Assessment,
  Download,
  Refresh,
  CheckCircle,
  Warning,
  Public,
  Lock,
  Info,
} from '@mui/icons-material';
import { accountService } from '../services/accountService';
import { apiService } from '../services/apiService';
import { AnalysisResult } from '../types';

// Bekannte Hersteller für Autocomplete
const KNOWN_MANUFACTURERS = [
  'Mould King',
  'CaDA',
  'Qman',
  'Sembo',
  'Pantasy',
  'Xingbao',
  'Keeppley',
  'Bluebrixx',
  'Cobi',
  'Wange',
  'LOZ',
  'Decool',
  'Lepin',
  'Kazi',
  'Enlighten',
  'Sluban',
];

const Upload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quota] = useState({ remaining: 200, total: 200 }); // TODO: Später aus API laden

  // Set-Informationen
  const [manufacturer, setManufacturer] = useState<string>('');
  const [setName, setSetName] = useState<string>('');
  const [setId, setSetId] = useState<string>('');
  const [isPublic, setIsPublic] = useState<boolean>(true);

  // useEffect(() => {
  //   brickAnalysisService.loadProtectedBricksDatabase();

  //   // Warte bis Authentifizierung abgeschlossen und Token gesetzt ist
  //   try {
  //     requireToken();

  //     updateQuota(); 
  //   } catch (error) {
  //     // nothing
  //   }

  // }, []);

  // const updateQuota = async () => {
  //   try {
  //     const userData = await accountService.getUserData();
  //     setQuota({
  //       remaining: accountService.getRemainingBricks(),
  //       total: userData.monthlyLimit,
  //     });
  //   } catch (error) {
  //     console.error('Error updating quota:', error);
  //     // Fallback to sync version
  //     const userData = accountService.getUserDataSync();
  //     setQuota({
  //       remaining: accountService.getRemainingBricks(),
  //       total: userData.monthlyLimit,
  //     });
  //   }
  // };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile: File) => {
    const validTypes = ['application/pdf', 'text/csv', 'application/vnd.ms-excel'];
    const validExtensions = ['.pdf', '.csv'];
    const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

    if (!validTypes.includes(selectedFile.type) && !validExtensions.includes(fileExtension)) {
      setError('Bitte wählen Sie eine PDF oder CSV Datei aus.');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setAnalysisResult(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    setAnalysisResult(null);
    setManufacturer('');
    setSetName('');
    setSetId('');
    setIsPublic(true);
  };

  const handleAnalyze = async () => {
    if (!file) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      // let brickIds: string[] = [];

      // if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      //   brickIds = await brickAnalysisService.analyzePDF(file);
      // } else if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      //   brickIds = await brickAnalysisService.analyzeCSV(file);
      // }

      // // Check quota
      // const quotaCheck = accountService.canAnalyze(brickIds.length);

      // if (!quotaCheck.allowed) {
      //   setError(
      //     `Nicht genügend Kontingent verfügbar!\n\nBenötigt: ${quotaCheck.requiredBricks} Steine\nVerfügbar: ${quotaCheck.remainingBricks} Steine\n\nBitte upgraden Sie Ihren Account oder warten Sie bis zum nächsten Monat.`
      //   );
      //   setIsAnalyzing(false);
      //   return;
      // }

      // // Analyze bricks
      // const result = brickAnalysisService.analyzeBricks(brickIds);
      // setAnalysisResult(result);

      // // Track usage and save to API
      // const documentId = 'doc-' + Date.now();
      // await accountService.addAnalysis(result.total, documentId);

      // Save document via API (upload file)
      try {
        await apiService.uploadDocument(
          file,
          isPublic,
          manufacturer.trim() || undefined,
          setName.trim() || undefined,
          setId.trim() || undefined
        );
      } catch (uploadError) {
        console.error('Error uploading document to API:', uploadError);
        // Fallback to localStorage
        // saveDocumentToHistory(documentId, result);
      }

      // Update quota display
      // await updateQuota();
    } catch (err: any) {
      setError('Fehler bei der Analyse: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleExport = () => {
    if (!analysisResult) return;

    let csv = 'Stein-ID,Beschreibung,Status,Schutz-Details\n';

    analysisResult.bricks.forEach((brick) => {
      const status = brick.protected ? 'Geschützt' : 'Ungeschützt';
      csv += `"${brick.id}","${brick.description}","${status}","${brick.protectionDetails}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `klemmbausteine_analyse_${Date.now()}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNewAnalysis = () => {
    handleRemoveFile();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dokument Analysieren
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Laden Sie eine PDF oder CSV-Datei hoch, um Steine auf Design-Schutz zu prüfen
          </Typography>
        </Box>

        {/* Quota Card */}
        <Card elevation={2} sx={{ mb: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
          <CardContent>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Verfügbares Kontingent
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {quota.remaining.toLocaleString('de-DE')} / {quota.total.toLocaleString('de-DE')} Steine verfügbar
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(quota.remaining / quota.total) * 100}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Verbrauch: {accountService.getUsagePercentage().toFixed(1)}%
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Upload Section */}
        {!analysisResult && (
          <Card elevation={2}>
            <CardContent sx={{ p: 4 }}>
              {!file ? (
                <Paper
                  variant="outlined"
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    borderColor: isDragging ? 'primary.main' : 'divider',
                    bgcolor: isDragging ? 'primary.50' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.50',
                    },
                  }}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Datei hochladen
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Ziehen Sie eine Datei hierher oder klicken Sie zum Auswählen
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unterstützte Formate: PDF, CSV
                  </Typography>
                  <input
                    id="file-input"
                    type="file"
                    accept=".pdf,.csv"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </Paper>
              ) : (
                <Box>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <InsertDriveFile sx={{ fontSize: 48, color: 'primary.main' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {file.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={handleRemoveFile}
                    >
                      Entfernen
                    </Button>
                  </Paper>

                  {/* Set-Informationen - nur bei privaten Sets Pflichtfelder */}
                  <Paper elevation={1} sx={{ p: 3, mb: 3, opacity: isPublic ? 0.7 : 1, transition: 'opacity 0.2s' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Set-Informationen {!isPublic && <Chip label="Pflichtfelder" size="small" color="warning" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {isPublic
                        ? 'Optional: Geben Sie die Informationen zum Klemmbaustein-Set an'
                        : 'Bitte geben Sie die Informationen zum Klemmbaustein-Set an (Pflichtfelder bei privaten Sets)'
                      }
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          freeSolo
                          options={KNOWN_MANUFACTURERS}
                          value={manufacturer}
                          onChange={(_, newValue) => setManufacturer(newValue || '')}
                          onInputChange={(_, newInputValue) => setManufacturer(newInputValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Hersteller"
                              placeholder="z.B. Mould King"
                              required={!isPublic}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Set-Name"
                          placeholder="z.B. Racing Supercar GT-R"
                          value={setName}
                          onChange={(e) => setSetName(e.target.value)}
                          required={!isPublic}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Set-Nummer"
                          placeholder="z.B. 15059"
                          value={setId}
                          onChange={(e) => setSetId(e.target.value)}
                          required={!isPublic}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Sichtbarkeit */}
                  <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Sichtbarkeit
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={isPublic ? 'public' : 'private'}
                        onChange={(e) => setIsPublic(e.target.value === 'public')}
                      >
                        <FormControlLabel
                          value="public"
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Public sx={{ color: 'success.main' }} />
                              <Box>
                                <Typography variant="body1" fontWeight={500}>
                                  Öffentlich
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Das Set ist für alle Nutzer sichtbar und durchsuchbar
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{ mb: 1, alignItems: 'flex-start', py: 1 }}
                        />
                        <FormControlLabel
                          value="private"
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Lock sx={{ color: 'warning.main' }} />
                              <Box>
                                <Typography variant="body1" fontWeight={500}>
                                  Privat
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Das Set ist nur für Sie sichtbar
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* Info-Box für Kosten */}
                    <Alert
                      severity="info"
                      icon={<Info />}
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="body2">
                        <strong>Tipp:</strong> Bei öffentlichen Sets werden für die Analyse <strong>50% weniger Punkte</strong> verbraucht.
                        So tragen Sie zur Gemeinschaft bei und sparen gleichzeitig Ihr Kontingent.
                      </Typography>
                    </Alert>
                  </Paper>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={isAnalyzing ? <CircularProgress size={20} color="inherit" /> : <Assessment />}
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || (!isPublic && (!manufacturer.trim() || !setName.trim() || !setId.trim()))}
                    sx={{ py: 1.5 }}
                  >
                    {isAnalyzing ? 'Analysiere...' : 'Analyse starten'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results Section */}
        {analysisResult && (
          <Box>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Gesamt-Steine
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {analysisResult.total}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card elevation={2} sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Geschützte Steine
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="error.main">
                      {analysisResult.protected}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card elevation={2} sx={{ borderLeft: 4, borderColor: 'success.main' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Ungeschützte Steine
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main">
                      {analysisResult.unprotected}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Results Table */}
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Analyseergebnisse
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExport}
                    >
                      Exportieren
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleNewAnalysis}
                    >
                      Neue Analyse
                    </Button>
                  </Box>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Stein-ID</strong></TableCell>
                        <TableCell><strong>Beschreibung</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Schutz-Details</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisResult.bricks.map((brick) => (
                        <TableRow
                          key={brick.id}
                          sx={{
                            bgcolor: brick.protected ? 'error.50' : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Typography fontWeight={600}>{brick.id}</Typography>
                          </TableCell>
                          <TableCell>{brick.description}</TableCell>
                          <TableCell>
                            <Chip
                              icon={brick.protected ? <Warning /> : <CheckCircle />}
                              label={brick.protected ? 'Geschützt' : 'Ungeschützt'}
                              color={brick.protected ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{brick.protectionDetails}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Upload;
